import { calculateEthAmountFromUsd, ensureEthPrice } from '@components/Web3/useEthPrice'
import { Analytics } from 'hooks/useAnalytics'
import { queryClient } from 'use-towns-client'

export const trackTipOnMessage = (location: 'messageActions' | 'messageReaction') => {
    Analytics.getInstance().track('clicked tip on message', {
        location, // 'messageActions' | 'messageReaction'
    })
}

export const trackTipAmount = async (cents: number, ethAmount?: number) => {
    try {
        // Check if we're in fallback mode
        const fallbackQueryKey = ['ethPrice', 'fallback']
        const isFallbackMode = queryClient.getQueryData<boolean>(fallbackQueryKey) ?? false

        if (isFallbackMode) {
            // In fallback mode, we can't get ETH price, so just track with available data
            Analytics.getInstance().track('clicked tip amount', {
                tipAmount: ethAmount?.toString() || '--', // Use provided ETH amount or fallback
                tipAmountUsd: cents > 0 ? formatUsd(cents) : '--', // USD might not be available
                fallbackMode: true,
            })
            return
        }

        const ethPrice = await ensureEthPrice()
        const calculatedEthAmount = calculateEthAmountFromUsd({
            cents,
            ethPriceInUsd: ethPrice,
        })
        Analytics.getInstance().track('clicked tip amount', {
            tipAmount: calculatedEthAmount.formatted, // in ETH- eg 0.005
            tipAmountUsd: formatUsd(cents),
        })
    } catch (error) {
        console.error('Error tracking tip amount', error)
        // Fallback tracking when price is unavailable
        Analytics.getInstance().track('clicked tip amount', {
            tipAmount: ethAmount?.toString() || '--', // Use provided ETH amount or fallback
            tipAmountUsd: cents > 0 ? formatUsd(cents) : '--',
            error: true,
        })
    }
}

export const trackPostedTip = (args: {
    cents: number
    tipAmount: string
    recipient: string
    spaceName: string
    spaceId: string
    isGated: boolean | undefined
    pricingModule: 'fixed' | 'dynamic' | 'free' | undefined
}) => {
    const { tipAmount, recipient, spaceName, spaceId, isGated, pricingModule, cents } = args
    Analytics.getInstance().track('posted tip', {
        tipAmountUsd: formatUsd(cents),
        tipAmount, // in ETH- eg 0.005
        recipient, // address
        spaceName,
        spaceId,
        isGated, // boolean
        pricingModule, // pricing model of space 'fixed' | 'dynamic' | 'free'
    })
}

export const trackPostedTipFailed = (args: {
    isGated: boolean | undefined
    pricingModule: 'fixed' | 'dynamic' | 'free' | undefined
    recipient: string
    spaceName: string
    spaceId: string
    tipAmount: string
    cents: number
}) => {
    const { cents, isGated, pricingModule, recipient, spaceName, spaceId, tipAmount } = args
    Analytics.getInstance().track('posted tip failed', {
        isGated,
        pricingModule,
        recipient,
        spaceName,
        spaceId,
        tipAmount,
        tipAmountUsd: formatUsd(cents),
    })
}

function formatUsd(cents: number) {
    return (cents / 100)
        .toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
        })
        .replace('$', '')
}
